import { useEffect, useState } from "react";
import { UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { InboxShell } from "@/pages/inbox/InboxShell";
import { useAuth } from "@/contexts/AuthContext";
import { deptInboxApi } from "@/lib/api";

/** Dean (8–9 Oct): a department inbox with a second tab for the signed-in person's own work email — identical front end,
 *  AI triage yes, Autopilot never. The tab only shows when that mailbox actually exists (SimplyFunded sign-in + Graph probe). */
export function InboxWithMine({ dept, title, icon: Icon, testid, caseLink, prefix, storageKey, region }) {
  const { user } = useAuth();
  const [view, setView] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem(storageKey)) || dept);
  const [mine, setMine] = useState({ checked: false, available: false, mailbox: "" });
  const devActor = typeof localStorage !== "undefined" ? localStorage.getItem("sf_dev_actor") : "";
  const myEmail = mine.mailbox || devActor || user?.email || "";

  useEffect(() => {
    let alive = true;
    deptInboxApi("mine", region || "UK").available()
      .then(r => alive && setMine({ checked: true, available: !!r.available, mailbox: r.mailbox || "", reason: r.reason }))
      .catch(() => alive && setMine({ checked: true, available: false, mailbox: "" }));
    return () => { alive = false; };
  }, [user?.email, devActor, region]);

  useEffect(() => {
    if (mine.checked && !mine.available && view === "mine") setView(dept);
  }, [mine.checked, mine.available, view, dept]);

  const pick = (id) => { setView(id); try { localStorage.setItem(storageKey, id); } catch { /* ignore */ } };
  const views = [
    { id: dept, label: title, icon: Icon },
    ...(mine.available ? [{ id: "mine", label: `My mailbox${myEmail ? ` (${myEmail})` : ""}`, icon: UserRound }] : []),
  ];
  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] min-h-0" data-testid={`${prefix}-inbox-page`}>
      {views.length > 1 && (
        <div className="flex items-center gap-1 border-b px-2 shrink-0" data-testid={`${prefix}-view-tabs`}>
          {views.map(v => (
            <button key={v.id} onClick={() => pick(v.id)}
              className={cn("flex items-center gap-1.5 px-4 py-2 text-sm transition border-b-2 -mb-px",
                view === v.id ? "border-blue-600 font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800")}
              data-testid={`${prefix}-view-tab-${v.id}`}>
              <v.icon className="w-4 h-4" />{v.label}
            </button>
          ))}
        </div>
      )}
      {view === "mine" && mine.available ? (
        <InboxShell key="mine" dept="mine" title="My mailbox" icon={UserRound} testid="my-mailbox-shell" caseLink={caseLink} embedded region={region}
          hideAutopilot subtitle="your own work email — AI triage on, Autopilot never; reply, log to files and work from the portal" />
      ) : (
        <InboxShell key={`${dept}-${region || "ctx"}`} dept={dept} title={title} icon={Icon} testid={testid} caseLink={caseLink} embedded={views.length > 1} region={region} />
      )}
    </div>
  );
}
